/**
 * SignaturePad – Wrapper um react-signature-canvas
 * Behebt iOS-Touch-Offset-Bug: Canvas-Breite wird auf tatsächliche
 * Container-Breite gesetzt, sodass Finger-Position 1:1 stimmt.
 */
import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import SignatureCanvas from 'react-signature-canvas';

const SignaturePad = forwardRef(
  ({ height = 200, penColor = '#1E40AF', onEnd, onBegin, className = '' }, ref) => {
    const innerRef = useRef(null);
    const containerRef = useRef(null);

    // Ref nach außen weiterleiten
    useImperativeHandle(ref, () => innerRef.current, []);

    const lastWidthRef = useRef(0);

    useEffect(() => {
      const resize = () => {
        if (!innerRef.current || !containerRef.current) return;
        const w = containerRef.current.offsetWidth;
        // Nur bei echter Breitenänderung neu dimensionieren (sonst würde jeder
        // ResizeObserver-Callback die Unterschrift löschen / Stroke crashen)
        if (w <= 0 || w === lastWidthRef.current) return;
        lastWidthRef.current = w;
        const sig = innerRef.current;
        let saved = null;
        try { saved = sig.toData(); } catch (e) { saved = null; }
        const canvas = sig.getCanvas();
        canvas.width = w;
        canvas.height = height;
        sig.clear();
        if (saved && saved.length) {
          try { sig.fromData(saved); } catch (e) { /* ignore */ }
        }
      };

      // Initial + nach kurzem Delay (Dialog-Animation abwarten)
      const t = setTimeout(resize, 120);

      // Auch bei Größenänderung (z.B. Rotation)
      const ro = new ResizeObserver(resize);
      if (containerRef.current) ro.observe(containerRef.current);

      return () => {
        clearTimeout(t);
        ro.disconnect();
      };
    }, [height]);

    return (
      <div ref={containerRef} style={{ width: '100%', display: 'block' }}>
        <SignatureCanvas
          ref={innerRef}
          penColor={penColor}
          onEnd={onEnd}
          onBegin={onBegin}
          canvasProps={{
            height,
            className: `w-full ${className}`,
            style: { touchAction: 'none', display: 'block' },
          }}
        />
      </div>
    );
  }
);

SignaturePad.displayName = 'SignaturePad';
export default SignaturePad;
